import { useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';

export interface KitchenOrderItem {
  id: string;
  name: string;
  quantity: number;
  modifiers?: Array<{ id: string; name: string; price: number }>;
  notes?: string;
}

export interface KitchenOrder {
  id: string;
  order_id: string;
  station_id: string | null;
  status: 'pending' | 'in_progress' | 'ready' | 'completed' | 'recalled';
  priority: number;
  items: KitchenOrderItem[];
  sent_at: string;
  started_at: string | null;
  completed_at: string | null;
  order?: {
    id: string;
    order_number: number;
    order_type: 'dine_in' | 'takeout' | 'delivery' | 'bar_tab';
    table?: { name: string } | null;
    server?: { first_name: string | null; last_name: string | null } | null;
    notes?: string;
  };
  station?: {
    id: string;
    name: string;
  } | null;
}

export interface KitchenStation {
  id: string;
  location_id: string;
  name: string;
  display_categories: string[] | null;
  printer_config: Record<string, unknown> | null;
  is_active: boolean;
}

// Timing thresholds in minutes
export const TIMING_THRESHOLDS = {
  green: 5,    // Under 5 minutes - good
  yellow: 10,  // 5-10 minutes - warning
  red: 15,     // Over 10 minutes - overdue
};

export function getTimingStatus(sentAt: string): 'green' | 'yellow' | 'red' {
  const minutesElapsed = (Date.now() - new Date(sentAt).getTime()) / 60000;
  
  if (minutesElapsed < TIMING_THRESHOLDS.green) return 'green';
  if (minutesElapsed < TIMING_THRESHOLDS.yellow) return 'yellow';
  return 'red';
}

export function getElapsedTime(sentAt: string): string {
  const totalSeconds = Math.floor((Date.now() - new Date(sentAt).getTime()) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function useKitchenOrders(stationId?: string | null) {
  const queryClient = useQueryClient();
  const { currentLocation } = useLocationStore();
  const locationId = currentLocation?.id;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previousOrderCount = useRef<number>(0);

  // Fetch kitchen stations
  const {
    data: stations,
    isLoading: stationsLoading,
  } = useQuery({
    queryKey: ['kitchen-stations', locationId],
    queryFn: async () => {
      if (!locationId) return [];

      const { data, error } = await supabase
        .from('kitchen_stations')
        .select('*')
        .eq('location_id', locationId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data as KitchenStation[];
    },
    enabled: !!locationId,
  });

  // Fetch kitchen orders
  const {
    data: orders,
    isLoading: ordersLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['kitchen-orders', locationId, stationId],
    queryFn: async () => {
      if (!locationId) return [];

      // Query kitchen orders directly and filter by location through the join
      // This ensures we get all active kitchen orders regardless of parent order status
      let query = supabase
        .from('kitchen_orders')
        .select(`
          *,
          order:orders!inner(
            id,
            order_number,
            order_type,
            notes,
            location_id,
            table:tables(name),
            server:profiles!orders_user_id_fkey(first_name, last_name)
          ),
          station:kitchen_stations(id, name)
        `)
        .eq('order.location_id', locationId)
        .in('status', ['pending', 'in_progress', 'ready'])
        .order('priority', { ascending: false })
        .order('sent_at', { ascending: true });

      // Filter by station if specified
      if (stationId) {
        query = query.eq('station_id', stationId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as KitchenOrder[];
    },
    enabled: !!locationId,
    refetchInterval: 20000, // Backup poll; realtime subscription handles live updates
  });

  // Play sound for new orders
  const playNewOrderSound = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio('/sounds/new-order.mp3');
      audioRef.current.volume = 0.7;
    }
    audioRef.current.play().catch(() => {
      // Audio play failed - user hasn't interacted with page yet
    });
  }, []);

  // Check for new orders and play sound
  useEffect(() => {
    if (orders && orders.length > previousOrderCount.current) {
      const newOrdersCount = orders.length - previousOrderCount.current;
      if (previousOrderCount.current > 0 && newOrdersCount > 0) {
        playNewOrderSound();
      }
    }
    previousOrderCount.current = orders?.length ?? 0;
  }, [orders, playNewOrderSound]);

  // Subscribe to realtime updates
  useEffect(() => {
    if (!locationId) return;

    const channel = supabase
      .channel(`kitchen-orders:${locationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'kitchen_orders',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['kitchen-orders', locationId, stationId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [locationId, stationId, queryClient]);

  // Start cooking (mark as in_progress)
  const startOrder = useMutation({
    mutationFn: async ({ kitchenOrderId }: { kitchenOrderId: string }) => {
      const { data, error } = await supabase
        .from('kitchen_orders')
        .update({
          status: 'in_progress',
          started_at: new Date().toISOString(),
        })
        .eq('id', kitchenOrderId)
        .select()
        .single();

      if (error) throw error;

      // Sync parent order status to in_progress (if still 'sent')
      if (data?.order_id) {
        await supabase
          .from('orders')
          .update({ status: 'in_progress' })
          .eq('id', data.order_id)
          .eq('status', 'sent'); // Only update if currently 'sent'
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders', locationId, stationId] });
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
    },
  });

  // Bump order (mark as ready)
  const bumpOrder = useMutation({
    mutationFn: async ({ kitchenOrderId }: { kitchenOrderId: string }) => {
      const { data, error } = await supabase
        .from('kitchen_orders')
        .update({
          status: 'ready',
          completed_at: new Date().toISOString(),
        })
        .eq('id', kitchenOrderId)
        .select()
        .single();

      if (error) throw error;

      // Check if ALL kitchen orders for this parent order are now ready or completed
      if (data?.order_id) {
        const { data: siblings } = await supabase
          .from('kitchen_orders')
          .select('status')
          .eq('order_id', data.order_id);

        const allReadyOrCompleted = siblings?.every(
          (s: { status: string }) => s.status === 'ready' || s.status === 'completed'
        );

        if (allReadyOrCompleted) {
          await supabase
            .from('orders')
            .update({ status: 'ready' })
            .eq('id', data.order_id)
            .in('status', ['sent', 'in_progress']); // Only update if in these states
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders', locationId, stationId] });
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
    },
  });

  // Complete order (mark as completed - removes from view)
  const completeOrder = useMutation({
    mutationFn: async ({ kitchenOrderId }: { kitchenOrderId: string }) => {
      const { data, error } = await supabase
        .from('kitchen_orders')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
        })
        .eq('id', kitchenOrderId)
        .select()
        .single();

      if (error) throw error;

      // Check if ALL kitchen orders for this parent order are now completed
      if (data?.order_id) {
        const { data: siblings } = await supabase
          .from('kitchen_orders')
          .select('status')
          .eq('order_id', data.order_id);

        const allCompleted = siblings?.every(
          (s: { status: string }) => s.status === 'completed'
        );

        if (allCompleted) {
          await supabase
            .from('orders')
            .update({ 
              status: 'completed',
              completed_at: new Date().toISOString(),
            })
            .eq('id', data.order_id)
            .in('status', ['sent', 'in_progress', 'ready']); // Only update if in these states
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders', locationId, stationId] });
      queryClient.invalidateQueries({ queryKey: ['orders', locationId] });
    },
  });

  // Recall order (bring back a completed order)
  const recallOrder = useMutation({
    mutationFn: async ({ kitchenOrderId }: { kitchenOrderId: string }) => {
      const { data, error } = await supabase
        .from('kitchen_orders')
        .update({
          status: 'recalled',
          completed_at: null,
        })
        .eq('id', kitchenOrderId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders', locationId, stationId] });
    },
  });

  // Set priority (rush order)
  const setPriority = useMutation({
    mutationFn: async ({ kitchenOrderId, priority }: { kitchenOrderId: string; priority: number }) => {
      const { data, error } = await supabase
        .from('kitchen_orders')
        .update({ priority })
        .eq('id', kitchenOrderId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders', locationId, stationId] });
    },
  });

  // Get all-day counts (total quantities for each item)
  const allDayCounts = orders?.reduce((acc, order) => {
    order.items.forEach((item) => {
      const key = item.name;
      acc[key] = (acc[key] || 0) + item.quantity;
    });
    return acc;
  }, {} as Record<string, number>) ?? {};

  return {
    orders: orders ?? [],
    stations: stations ?? [],
    allDayCounts,
    isLoading: ordersLoading || stationsLoading,
    error,
    refetch,
    startOrder,
    bumpOrder,
    completeOrder,
    recallOrder,
    setPriority,
  };
}

